import React, { useMemo } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useApHub } from './ApHubContext';
import { harmonateToolbarBuFieldSx } from '../../../theme/harmonateStyles';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';

const fieldSx = {
  minWidth: 300,
  bgcolor: ht.white,
  '& .MuiOutlinedInput-root': {
    minHeight: 40,
    height: 40,
    boxSizing: 'border-box',
  },
};

const toolbarAutocompleteSx = {
  ...harmonateToolbarBuFieldSx,
  '& .MuiAutocomplete-popupIndicator': {
    color: ht.white,
  },
  '& .MuiAutocomplete-clearIndicator': {
    color: 'rgba(255, 255, 255, 0.75)',
  },
  '& .MuiAutocomplete-endAdornment': {
    right: 6,
  },
  '& .MuiOutlinedInput-root': {
    bgcolor: 'transparent',
    borderRadius: '8px !important',
    height: 40,
    alignItems: 'center',
    color: ht.white,
    paddingRight: '36px !important',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.75) !important',
      borderWidth: '1px',
      borderRadius: '8px',
    },
    '&:hover fieldset': {
      borderColor: `${ht.white} !important`,
    },
    '&.Mui-focused fieldset': {
      borderColor: `${ht.white} !important`,
      borderWidth: '1px',
    },
  },
  '& .MuiOutlinedInput-input, & .MuiInputBase-input, & input': {
    px: 1.25,
    fontSize: '0.875rem',
    color: `${ht.white} !important`,
    WebkitTextFillColor: ht.white,
    py: 0,
    caretColor: ht.white,
    '&::placeholder': {
      color: 'rgba(255, 255, 255, 0.85) !important',
      WebkitTextFillColor: 'rgba(255, 255, 255, 0.85)',
      opacity: '1 !important',
    },
    '&::-webkit-input-placeholder': {
      color: 'rgba(255, 255, 255, 0.85) !important',
      WebkitTextFillColor: 'rgba(255, 255, 255, 0.85)',
      opacity: '1 !important',
    },
    '&::-moz-placeholder': {
      color: 'rgba(255, 255, 255, 0.85) !important',
      opacity: '1 !important',
    },
  },
};

export const formatApBusinessUnitLabel = (unit) => {
  if (!unit) return '';
  const code = unit.businessUnitCode || unit.propertyCode || '';
  const name = unit.businessUnitName || unit.propertyName || '';
  if (code && name) return `${code} - ${name}`;
  return name || code || '';
};

const ApBusinessUnitSelector = ({ sx = {}, variant = 'default' }) => {
  const {
    businessUnits,
    businessUnitsLoading,
    selectedBusinessUnitId,
    selectedBusinessUnitName,
    setSelectedBusinessUnitId,
    canSelectBusinessUnit,
  } = useApHub();

  const isToolbar = variant === 'harmonate-toolbar';
  const resolvedSx = isToolbar
    ? { ...toolbarAutocompleteSx, ...sx }
    : { ...fieldSx, ...sx };

  const selectedBusinessUnit = useMemo(
    () => businessUnits.find((bu) => String(bu.businessUnitId) === String(selectedBusinessUnitId)) || null,
    [businessUnits, selectedBusinessUnitId]
  );

  if (!canSelectBusinessUnit) {
    return (
      <TextField
        size="small"
        label="Business Unit"
        value={selectedBusinessUnitName || ''}
        disabled
        InputLabelProps={{ shrink: true }}
        placeholder="Select on Dashboard"
        sx={resolvedSx}
      />
    );
  }

  return (
    <Autocomplete
      size="small"
      options={businessUnits}
      value={selectedBusinessUnit}
      loading={businessUnitsLoading}
      disabled={businessUnitsLoading}
      onChange={(_, value) => setSelectedBusinessUnitId(value?.businessUnitId || '')}
      getOptionLabel={formatApBusinessUnitLabel}
      isOptionEqualToValue={(option, value) =>
        String(option?.businessUnitId) === String(value?.businessUnitId)
      }
      ListboxProps={{ style: { maxHeight: 280 } }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Business Unit"
          placeholder="Select Business Unit"
          InputLabelProps={{
            ...params.InputLabelProps,
            shrink: true,
          }}
          inputProps={{
            ...params.inputProps,
            style: {
              ...(params.inputProps?.style || {}),
              ...(isToolbar
                ? {
                    color: ht.white,
                    WebkitTextFillColor: ht.white,
                    caretColor: ht.white,
                  }
                : {}),
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {businessUnitsLoading ? (
                  <CircularProgress color="inherit" size={18} sx={isToolbar ? { color: ht.white } : undefined} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={
            isToolbar
              ? {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.75) !important',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: `${ht.white} !important`,
                  },
                  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: `${ht.white} !important`,
                  },
                  '& input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.85) !important',
                    WebkitTextFillColor: 'rgba(255, 255, 255, 0.85)',
                    opacity: '1 !important',
                  },
                  '& input::-webkit-input-placeholder': {
                    color: 'rgba(255, 255, 255, 0.85) !important',
                    WebkitTextFillColor: 'rgba(255, 255, 255, 0.85)',
                    opacity: '1 !important',
                  },
                }
              : undefined
          }
        />
      )}
      sx={resolvedSx}
    />
  );
};

export default ApBusinessUnitSelector;
